<template>
    <v-container class="settings-container mb-12 mb-lg-0">
        <v-overlay :value="loading">
            <v-progress-circular indeterminate width="6" size="48"/>
        </v-overlay>

        <v-row justify="center">
            <v-col cols="12" xl="11">
                <v-card class="settings-heading-card">
                    <v-card-title class="settings-main-title primary secondary--text d-flex align-center" style="word-break: normal">
                        <v-icon color="secondary" class="mr-2">mdi-shield-key</v-icon>
                        <span class="font-weight-bold">{{ $vuetify.breakpoint.xsOnly ? 'Ajustes' : 'CONFIGURACION Y SEGURIDAD' }}</span>
                        <v-spacer/>
                        <v-btn icon color="secondary" :loading="saving" @click="savePreferences">
                            <v-icon>mdi-content-save</v-icon>
                        </v-btn>
                        <v-btn icon color="secondary" :loading="loading" @click="getPreferences">
                            <v-icon>mdi-sync</v-icon>
                        </v-btn>
                    </v-card-title>
                </v-card>
            </v-col>
        </v-row>

        <v-row justify="center">
            <v-col cols="12" xl="11">
                <v-row dense>
                    <v-col cols="12" md="6" lg="3">
                        <v-card outlined class="settings-panel fill-height">
                            <v-card-title class="settings-panel-title">
                                <v-icon color="primary" class="mr-2">mdi-theme-light-dark</v-icon>
                                Apariencia
                            </v-card-title>
                            <v-card-text class="settings-panel-body">
                                <v-btn-toggle
                                    v-model="form.theme_preference"
                                    mandatory
                                    dense
                                    class="d-flex mb-4 settings-toggle"
                                    @change="setTheme">
                                    <v-btn value="system" class="flex-grow-1">
                                        <v-icon left>mdi-theme-light-dark</v-icon>Sistema
                                    </v-btn>
                                    <v-btn value="light" class="flex-grow-1">
                                        <v-icon left>mdi-white-balance-sunny</v-icon>Claro
                                    </v-btn>
                                    <v-btn value="dark" class="flex-grow-1">
                                        <v-icon left>mdi-weather-night</v-icon>Oscuro
                                    </v-btn>
                                </v-btn-toggle>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <v-col cols="12" md="6" lg="3">
                        <v-card outlined class="settings-panel fill-height">
                            <v-card-title class="settings-panel-title">
                                <v-icon color="primary" class="mr-2">mdi-bell-cog</v-icon>
                                Notificaciones
                            </v-card-title>
                            <v-card-text class="settings-panel-body">
                                <v-switch
                                    v-model="form.notifications_enabled"
                                    color="success"
                                    inset
                                    dense
                                    class="settings-switch"
                                    :disabled="saving"
                                    label="Recibir notificaciones en mis dispositivos"
                                    @change="savePreferences()"/>
                                <v-switch
                                    v-model="form.security_notifications_enabled"
                                    color="success"
                                    inset
                                    dense
                                    class="settings-switch"
                                    :disabled="saving"
                                    label="Avisos de seguridad y accesos"
                                    @change="savePreferences()"/>
                                <v-list two-line color="transparent" class="pa-0 mt-2">
                                    <v-list-item class="settings-list-item">
                                        <v-list-item-icon>
                                            <v-icon :color="notificationState.push_ready ? 'success' : 'warning'">mdi-bell-ring</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-content>
                                            <v-list-item-title>{{ notificationState.push_ready ? 'Dispositivo listo' : 'Falta activar este dispositivo' }}</v-list-item-title>
                                            <v-list-item-subtitle>{{ notificationDeviceText }}</v-list-item-subtitle>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list>
                                <v-btn
                                    v-if="canUseWebPush"
                                    color="primary"
                                    block
                                    class="mt-3"
                                    :loading="notificationLoading"
                                    :disabled="saving || notificationLoading || !form.notifications_enabled"
                                    @click="activateNotifications">
                                    <v-icon left>mdi-bell-plus</v-icon>Activar en este dispositivo
                                </v-btn>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <v-col cols="12" md="6" lg="3">
                        <v-card outlined class="settings-panel fill-height">
                            <v-card-title class="settings-panel-title">
                                <v-icon color="primary" class="mr-2">mdi-account-cog</v-icon>
                                Cuenta
                            </v-card-title>
                            <v-card-text class="settings-panel-body">
                                <v-list two-line color="transparent" class="pa-0">
                                    <v-list-item class="settings-list-item">
                                        <v-list-item-avatar size="52">
                                            <img :src="'/' + (user.photo ? user.photo : 'img/photo-default.jpg')" :alt="displayName">
                                        </v-list-item-avatar>
                                        <v-list-item-content>
                                            <v-list-item-title class="font-weight-bold">{{ displayName }}</v-list-item-title>
                                            <v-list-item-subtitle>{{ accountTag }}</v-list-item-subtitle>
                                        </v-list-item-content>
                                    </v-list-item>
                                    <v-list-item class="settings-list-item">
                                        <v-list-item-icon><v-icon color="primary">mdi-email-check</v-icon></v-list-item-icon>
                                        <v-list-item-content>
                                            <v-list-item-title>{{ user.email || 'Sin correo' }}</v-list-item-title>
                                            <v-list-item-subtitle>Correo registrado</v-list-item-subtitle>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list>
                                <v-row dense class="mt-2">
                                    <v-col cols="12" sm="6">
                                        <v-btn block color="primary" outlined to="/user/edit">
                                            <v-icon left>mdi-account-edit</v-icon>Mis datos
                                        </v-btn>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-btn block color="primary" outlined to="/user/accounts">
                                            <v-icon left>mdi-bank-check</v-icon>Mis cuentas
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <v-col v-if="nativeCustomer" cols="12" md="6" lg="3">
                        <v-card outlined class="settings-panel fill-height">
                            <v-card-title class="settings-panel-title">
                                <v-icon color="primary" class="mr-2">mdi-cellphone-lock</v-icon>
                                Bloqueo
                            </v-card-title>
                            <v-card-text class="settings-panel-body">
                                <v-switch
                                    v-model="form.app_lock_enabled"
                                    color="success"
                                    inset
                                    dense
                                    class="settings-switch"
                                    :disabled="saving || isNativeApp"
                                    label="Pedir desbloqueo al volver a la app"/>
                                <v-btn-toggle
                                    v-if="!isNativeApp"
                                    v-model="form.app_lock_method"
                                    mandatory
                                    dense
                                    class="d-flex mb-3 settings-toggle settings-toggle-two">
                                    <v-btn value="pin" class="flex-grow-1">
                                        <v-icon left>mdi-numeric-4-box</v-icon>PIN
                                    </v-btn>
                                    <v-btn value="device" class="flex-grow-1">
                                        <v-icon left>mdi-fingerprint</v-icon>Dispositivo
                                    </v-btn>
                                </v-btn-toggle>
                                <v-row dense>
                                    <v-col cols="12" sm="6">
                                        <v-text-field
                                            v-model="appLockPin"
                                            label="PIN"
                                            type="password"
                                            inputmode="numeric"
                                            maxlength="4"
                                            counter="4"
                                            dense
                                            filled
                                            :disabled="saving"
                                            :error-messages="appLockErrors.app_lock_pin"
                                            prepend-icon="mdi-lock-reset"
                                            @input="clearAppLockError('app_lock_pin')"/>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-text-field
                                            v-model="appLockPinConfirmation"
                                            label="Confirmar"
                                            type="password"
                                            inputmode="numeric"
                                            maxlength="4"
                                            counter="4"
                                            dense
                                            filled
                                            :disabled="saving"
                                            prepend-icon="mdi-lock-check"/>
                                    </v-col>
                                </v-row>
                                <v-select
                                    v-model="form.app_lock_timeout_minutes"
                                    :items="lockTimeoutOptions"
                                    item-text="text"
                                    item-value="value"
                                    label="Cuando bloquear"
                                    dense
                                    filled
                                    :disabled="saving"
                                    prepend-icon="mdi-timer-lock"/>
                                <div class="caption grey--text text--darken-1">
                                    {{ appLockSummary }}
                                </div>
                                <v-btn
                                    block
                                    color="primary"
                                    class="secondary--text mt-3"
                                    :loading="saving"
                                    :disabled="saving"
                                    @click="savePreferences">
                                    <v-icon left>mdi-content-save</v-icon>Guardar bloqueo
                                </v-btn>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>

        <v-row justify="center">
            <v-col cols="12" xl="11">
                <password :user="user" embedded/>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    import axios from 'axios';
    import Password from './Password';
    import { settings } from '../config';
    import { devicePayload } from '../helpers/device';
    import { currentNotificationPermission, rememberPermissionState } from '../helpers/permissions';

    export default {
        name: "Settings",
        components: { Password },
        props: ['user'],
        data: () => ({
            loading: true,
            saving: false,
            notificationLoading: false,
            appLockPin: '',
            appLockPinConfirmation: '',
            appLockErrors: {},
            lockTimeoutOptions: [
                { text: 'Al salir de la app', value: 0 },
                { text: 'Despues de 1 minuto', value: 1 },
                { text: 'Despues de 5 minutos', value: 5 },
                { text: 'Despues de 15 minutos', value: 15 },
                { text: 'Despues de 30 minutos', value: 30 },
            ],
            form: {
                theme_preference: 'system',
                notifications_enabled: true,
                security_notifications_enabled: true,
                app_lock_enabled: false,
                app_lock_method: 'pin',
                app_lock_timeout_minutes: 0,
                app_lock_pin_set: false
            },
            notificationState: {
                push_ready: false,
                push_devices: 0,
                active_devices: 0
            },
            security: {
                two_factor: {
                    enabled: false,
                    email: false,
                    authenticator: false,
                    passkey: false,
                    passkeys: 0,
                    dynamic_key: false
                },
                passkey: {
                    enabled: false,
                    count: 0
                },
                password: {
                    expired: false,
                    expires_at: null
                }
            }
        }),
        computed: {
            isNativeApp() {
                return settings.is_native_app === true;
            },
            isCustomer() {
                return this.user && this.user.role === 'Customer';
            },
            nativeCustomer() {
                return this.isNativeApp && this.isCustomer;
            },
            canUseWebPush() {
                return !this.isNativeApp
                    && settings.onesignal_web_enabled !== false
                    && !!settings.onesignal_enabled
                    && !!settings.onesignal_app_id;
            },
            notificationDeviceText() {
                const devices = Number(this.notificationState.active_devices || 0);
                const push = Number(this.notificationState.push_devices || 0);

                if (!this.form.notifications_enabled) {
                    return 'Las notificaciones push estan desactivadas.';
                }

                if (this.isNativeApp) {
                    return 'La app usa las notificaciones propias del telefono.';
                }

                return push + ' de ' + devices + ' dispositivo(s) con push activo.';
            },
            appLockSummary() {
                if (this.nativeCustomer) {
                    return this.form.app_lock_pin_set && !this.appLockPin
                        ? 'PIN activo. Escribe uno nuevo solo si necesitas cambiarlo.'
                        : 'Configura un PIN de 4 numeros para usar la app.';
                }

                return '';
            },
            accountTag() {
                return '$' + (this.user.nickname || (this.user.id ? String(this.user.id).padStart(5, '0') : ''));
            },
            displayName() {
                return [this.user.name, this.user.lname].filter(Boolean).join(' ').trim() || 'Cliente Valzum';
            }
        },
        mounted() {
            this.getPreferences();
        },
        methods: {
            getPreferences() {
                this.loading = true;
                axios.get('/api/preferences').then(res => {
                    this.applyPayload(res.data);
                }).catch(err => {
                    this.$toast.error(this.errorMessage(err, 'No fue posible cargar configuracion.'), {icon: 'mdi-alert-circle'});
                }).then(() => {
                    this.loading = false;
                });
            },
            savePreferences(showToast = true) {
                this.appLockErrors = {};
                this.saving = true;
                const payload = Object.assign({}, this.form);
                delete payload.app_lock_pin_set;
                payload.device_info = this.devicePayload();

                if (this.nativeCustomer) {
                    payload.app_lock_enabled = true;
                    payload.app_lock_method = 'pin';
                } else {
                    delete payload.app_lock_enabled;
                    delete payload.app_lock_method;
                    delete payload.app_lock_timeout_minutes;
                }

                if (this.nativeCustomer && (this.appLockPin || this.appLockPinConfirmation)) {
                    payload.app_lock_pin = this.onlyDigits(this.appLockPin);
                    payload.app_lock_pin_confirmation = this.onlyDigits(this.appLockPinConfirmation);
                }

                axios.post('/api/preferences', payload).then(res => {
                    this.applyPayload(res.data);
                    this.appLockPin = '';
                    this.appLockPinConfirmation = '';
                    this.$emit('preferences-updated', res.data);
                    if (showToast !== false) {
                        this.$toast.success(res.data.status || 'Ajustes guardados.', {icon: 'mdi-check-circle'});
                    }
                }).catch(err => {
                    if (err.response && err.response.data && err.response.data.errors) {
                        this.appLockErrors = err.response.data.errors;
                    }
                    this.$toast.error(this.errorMessage(err, 'No fue posible guardar configuracion.'), {icon: 'mdi-alert-circle'});
                }).then(() => {
                    this.saving = false;
                });
            },
            applyPayload(data) {
                const payload = data || {};
                this.form = Object.assign({}, this.form, payload.preferences || {});
                if (this.nativeCustomer) {
                    this.form.app_lock_enabled = true;
                    this.form.app_lock_method = 'pin';
                } else {
                    this.form.app_lock_enabled = false;
                    this.form.app_lock_method = 'pin';
                    this.form.app_lock_pin_set = false;
                }
                this.notificationState = Object.assign({}, this.notificationState, payload.notifications || {});
                this.security = Object.assign({}, this.security, payload.security || {});
                this.applyTheme(this.form.theme_preference);

                if (payload.user) {
                    this.$store.dispatch('setUser', {user: payload.user});
                }
            },
            setTheme(mode) {
                this.form.theme_preference = mode || 'system';
                this.applyTheme(this.form.theme_preference);
                this.savePreferences(false);
            },
            applyTheme(mode) {
                const normalized = ['system', 'light', 'dark'].indexOf(mode) !== -1 ? mode : 'system';
                localStorage.setItem('theme_mode', normalized);
                localStorage.setItem('dark', normalized === 'dark' ? 'true' : 'false');

                const systemDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
                this.$vuetify.theme.dark = normalized === 'dark' || (normalized === 'system' && systemDark);

                if (window.CustomEvent) {
                    window.dispatchEvent(new CustomEvent('valzum-theme-change', {
                        detail: {mode: normalized}
                    }));
                }
            },
            activateNotifications() {
                if (!this.form.notifications_enabled) return;

                this.notificationLoading = true;
                this.ensurePushToken()
                    .then(() => axios.post('/api/notifications/device', this.devicePayload()))
                    .then(res => {
                        this.$toast.success(res.data.status || 'Notificaciones activas.', {icon: 'mdi-bell-check'});
                        this.getPreferences();
                        this.$emit('preferences-updated', {preferences: this.form});
                    })
                    .catch(err => {
                        this.$toast.error(this.errorMessage(err, 'No fue posible activar notificaciones en este dispositivo.'), {icon: 'mdi-alert-circle'});
                    })
                    .then(() => {
                        this.notificationLoading = false;
                    });
            },
            ensurePushToken() {
                if (!this.canUseWebPush || settings.onesignal_user_id) {
                    return Promise.resolve();
                }

                const existingPermission = currentNotificationPermission();
                if (existingPermission === 'denied') {
                    return Promise.reject(new Error('Las notificaciones estan bloqueadas en este dispositivo.'));
                }

                return new Promise((resolve, reject) => {
                    const init = () => {
                        window.OneSignalDeferred = window.OneSignalDeferred || [];
                        window.OneSignalDeferred.push(async OneSignal => {
                            const finish = async () => {
                                let permission = currentNotificationPermission();

                                if (OneSignal.Notifications && typeof OneSignal.Notifications.requestPermission === 'function') {
                                    if (permission !== 'granted') {
                                        await OneSignal.Notifications.requestPermission();
                                        permission = currentNotificationPermission();
                                    }
                                }

                                rememberPermissionState('notifications', permission);
                                if (permission === 'denied') {
                                    throw new Error('Las notificaciones estan bloqueadas en este dispositivo.');
                                }

                                const subscription = OneSignal.User && OneSignal.User.PushSubscription;
                                const subscriptionId = subscription ? (subscription.id || subscription.token || null) : null;
                                if (subscriptionId) {
                                    settings.onesignal_user_id = subscriptionId;
                                }
                            };

                            try {
                                if (!window.__valzumOneSignalInitialized) {
                                    await OneSignal.init({ appId: settings.onesignal_app_id });
                                    window.__valzumOneSignalInitialized = true;
                                }

                                await finish();
                                resolve();
                            } catch (error) {
                                const message = String(error && error.message ? error.message : error).toLowerCase();
                                if (message.indexOf('already') !== -1) {
                                    window.__valzumOneSignalInitialized = true;
                                    finish().then(resolve).catch(reject);
                                    return;
                                }

                                reject(error);
                            }
                        });
                    };

                    if (document.getElementById('onesignal-sdk')) {
                        init();
                        return;
                    }

                    const script = document.createElement('script');
                    script.id = 'onesignal-sdk';
                    script.src = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
                    script.defer = true;
                    script.onload = init;
                    script.onerror = reject;
                    document.head.appendChild(script);
                });
            },
            devicePayload() {
                const payload = devicePayload();
                payload.onesignal_user_id = settings.onesignal_user_id;

                return payload;
            },
            onlyDigits(value) {
                return String(value || '').replace(/\D/g, '').slice(0, 4);
            },
            clearAppLockError(field) {
                this.appLockPin = this.onlyDigits(this.appLockPin);
                this.appLockPinConfirmation = this.onlyDigits(this.appLockPinConfirmation);
                if (this.appLockErrors[field]) {
                    this.$delete(this.appLockErrors, field);
                }
            },
            errorMessage(err, fallback) {
                if (err.response && err.response.data && err.response.data.status) {
                    return err.response.data.status;
                }

                if (err.response && err.response.data && err.response.data.message) {
                    return err.response.data.message;
                }

                return fallback;
            }
        }
    }
</script>

<style scoped>
.settings-heading-card {
    overflow: hidden;
}

.settings-main-title {
    line-height: 1.25;
}

.settings-panel {
    border-color: rgba(13, 147, 165, 0.18);
}

.settings-panel-title {
    font-weight: 700;
    line-height: 1.25;
    word-break: normal;
}

.settings-panel-body {
    padding-top: 10px;
}

.settings-switch {
    margin-top: 0;
}

.settings-list-item {
    min-height: 64px;
}

.settings-toggle {
    width: 100%;
}

@media (max-width: 600px) {
    .settings-container {
        padding-left: 10px;
        padding-right: 10px;
    }

    .settings-main-title {
        font-size: 1rem;
        min-height: 50px;
        padding: 10px 12px;
    }

    .settings-main-title .v-btn {
        height: 36px;
        width: 36px;
    }

    .settings-panel {
        border-radius: 8px;
    }

    .settings-panel-title {
        font-size: .98rem;
        min-height: 48px;
        padding: 10px 12px 6px;
    }

    .settings-panel-body {
        padding: 6px 12px 12px !important;
    }

    .settings-toggle {
        height: auto !important;
    }

    .settings-toggle .v-btn {
        min-width: 0;
        padding: 0 6px;
        width: 33.333%;
    }

    .settings-toggle .v-btn ::v-deep .v-icon {
        margin-right: 4px !important;
    }

    .settings-switch {
        margin-bottom: 0;
    }

    .settings-switch ::v-deep .v-label {
        font-size: .88rem;
        line-height: 1.25;
    }

    .settings-list-item {
        min-height: 54px;
        padding-left: 0;
        padding-right: 0;
    }

    .settings-list-item ::v-deep .v-list-item__icon {
        margin-right: 12px;
    }

    .settings-list-item ::v-deep .v-list-item__title,
    .settings-list-item ::v-deep .v-list-item__subtitle {
        white-space: normal;
    }
}
</style>
